import { Box, Button, Flex, HStack, Heading, Stack, Text, VStack, keyframes } from "@chakra-ui/react";
import React from "react";
import { Link } from "react-router-dom";
import { FiLock, FiGift, FiUser, FiCheck, FiShield, FiArrowRight } from "react-icons/fi";
import { ApiVotingListItem } from "@lubbezposrednio-frontend/core";

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const orbFloat = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(34px, -22px) scale(1.12); }
  66% { transform: translate(-22px, 22px) scale(0.94); }
`;
const floatFront = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-14px); }
`;
const floatBack = keyframes`
  0%, 100% { transform: translateY(0px) rotate(6deg); }
  50% { transform: translateY(-9px) rotate(6deg); }
`;
const floatDecoA = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-16px) rotate(8deg); }
`;
const floatDecoB = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(13px) rotate(-8deg); }
`;

const DecoBadge: React.FC<{
  icon: React.ElementType;
  color: string;
  bg: string;
  anim: string;
  pos: Record<string, string>;
  iconColor?: string;
}> = ({ icon: Icon, color, bg, anim, pos, iconColor }) => (
  <Flex
    position="absolute"
    zIndex={3}
    display={{ base: "none", lg: "flex" }}
    w="48px"
    h="48px"
    borderRadius="16px"
    bg={bg}
    border="1px solid rgba(255,255,255,0.55)"
    backdropFilter="blur(8px)"
    align="center"
    justify="center"
    boxShadow={`0 14px 32px ${color}`}
    sx={{ animation: anim, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
    {...pos}
  >
    <Icon size={21} color={iconColor} />
  </Flex>
);

type HeroVoting = {
  id?: string;
  title?: string;
  end_date?: string;
  user?: ApiVotingListItem["user"];
  question_preview?: { content: string; options: string[]; more: number } | null;
};

// Zaślepka opcji tylko gdy głosowanie nie niesie podglądu pytania (np. mock FALLBACK).
const OPTIONS = ["Tak, popieram", "Nie", "Wstrzymuję się"];

const FALLBACK: HeroVoting[] = [
  {
    id: "a",
    title: "Budżet osiedlowy 2026",
    user: { email: "", first_name: "Jan", last_name: "Kowalski" },
    question_preview: { content: "Budżet osiedlowy 2026", options: ["Plac zabaw", "Remont chodników", "Zieleń i ławki"], more: 0 }
  },
  {
    id: "b",
    title: "Nazwa nowej świetlicy",
    user: { email: "", organization_name: "Rada Osiedla", is_organization: true }
  }
];

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short" }) : "";

const organizerName = (u?: ApiVotingListItem["user"]) =>
  !u
    ? "Organizator"
    : u.is_organization
    ? u.organization_name || u.email
    : [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;

const StatusChip: React.FC = () => (
  <HStack spacing="7px" bg="rgba(47,206,37,0.14)" px="10px" py="4px" borderRadius="full">
    <Box w="7px" h="7px" borderRadius="full" bg="#2FCE25" />
    <Text fontSize="12px" fontWeight="700" color="#1f9e18">
      Trwa
    </Text>
  </HStack>
);

const TrustChip: React.FC<{ icon: React.ElementType; label: string }> = ({ icon: Icon, label }) => (
  <HStack spacing="8px" bg="rgba(255,255,255,0.1)" border="1px solid rgba(255,255,255,0.18)" backdropFilter="blur(10px)" borderRadius="full" px="14px" py="7px">
    <Icon size={15} color="#FFFFFF" />
    <Text fontSize="13px" fontWeight="600" color="white">
      {label}
    </Text>
  </HStack>
);

const VotingPreview: React.FC<{ votings: HeroVoting[] }> = ({ votings }) => {
  const list = votings.length ? votings : FALLBACK;
  const front = list[0];
  const back = list[1];

  // Realne odpowiedzi z podglądu pytania; zaślepka tylko gdy brak (mock).
  const frontOptions = front?.question_preview?.options?.length ? front.question_preview.options : OPTIONS;
  const frontMore = front?.question_preview?.more ?? 0;

  return (
    <Flex flex="1" position="relative" justify="center" align="center" minH={{ base: "360px", lg: "440px" }} w="100%">
      <Box
        position="absolute"
        w={{ base: "300px", lg: "420px" }}
        h={{ base: "300px", lg: "420px" }}
        bgGradient="radial(circle, rgba(229,31,68,0.55), transparent 70%)"
        filter="blur(30px)"
        zIndex={0}
      />

      {/* floatujące ozdoby */}
      <DecoBadge icon={FiCheck} bg="white" iconColor="#1f9e18" color="rgba(0,0,0,0.18)" anim={`${floatDecoA} 5.5s ease-in-out infinite`} pos={{ top: "4%", right: "8%" }} />
      <DecoBadge icon={FiLock} bg="white" iconColor="#430098" color="rgba(0,0,0,0.18)" anim={`${floatDecoB} 6.5s ease-in-out infinite`} pos={{ bottom: "12%", left: "2%" }} />
      <DecoBadge icon={FiShield} bg="#E51F44" iconColor="#FFFFFF" color="rgba(229,31,68,0.45)" anim={`${floatDecoA} 7.5s ease-in-out infinite reverse`} pos={{ bottom: "26%", right: "1%" }} />

      {/* drobne kształty */}
      <Box position="absolute" zIndex={1} display={{ base: "none", lg: "block" }} top="13%" left="11%" w="34px" h="34px" borderRadius="full" border="3px solid rgba(255,255,255,0.4)" sx={{ animation: `${floatDecoB} 8s ease-in-out infinite` }} />
      <Box position="absolute" zIndex={1} display={{ base: "none", lg: "block" }} top="42%" right="13%" w="14px" h="14px" borderRadius="full" bg="rgba(255,255,255,0.6)" sx={{ animation: `${floatDecoA} 5s ease-in-out infinite` }} />
      <Box position="absolute" zIndex={1} display={{ base: "none", lg: "block" }} bottom="6%" left="24%" w="11px" h="11px" borderRadius="full" bg="rgba(255,255,255,0.45)" sx={{ animation: `${floatDecoB} 6s ease-in-out infinite` }} />

      {back && (
        <Box
          position="absolute"
          top="-30px"
          right={{ lg: "0px" }}
          w="290px"
          bg="white"
          borderRadius="20px"
          p="20px"
          boxShadow="0 18px 44px rgba(0,0,0,0.18)"
          zIndex={1}
          display={{ base: "none", lg: "block" }}
          sx={{ animation: `${floatBack} 7s ease-in-out infinite`, "@media (prefers-reduced-motion: reduce)": { animation: "none", transform: "rotate(6deg)" } }}
        >
          <StatusChip />
          <Heading fontSize="16px" color="primary" noOfLines={1} mt="10px">
            {back.title}
          </Heading>
          <Text fontSize="13px" color="gray.500" mt="6px">
            {organizerName(back.user)}
          </Text>
        </Box>
      )}

      <Box
        position="relative"
        zIndex={2}
        bg="white"
        borderRadius="22px"
        p={{ base: "20px", md: "26px" }}
        w={{ base: "300px", md: "360px" }}
        boxShadow="0 30px 70px rgba(0,0,0,0.30)"
        sx={{ animation: `${floatFront} 6s ease-in-out infinite`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
      >
        <HStack justify="space-between" mb="14px">
          <StatusChip />
          {formatDate(front.end_date) && (
            <Text fontSize="12px" color="gray.500" fontWeight="600">
              Do {formatDate(front.end_date)}
            </Text>
          )}
        </HStack>

        <Heading fontSize="20px" lineHeight="26px" color="primary" noOfLines={2} mb="8px">
          {front.title}
        </Heading>
        <HStack spacing="7px" color="gray.500" fontSize="13px" mb="18px">
          <FiUser size={14} />
          <Text noOfLines={1}>{organizerName(front.user)}</Text>
        </HStack>

        <VStack spacing="10px" align="stretch">
          {frontOptions.slice(0, 3).map((o, i) => (
            <HStack
              key={`${o}-${i}`}
              spacing="12px"
              p="11px 14px"
              borderRadius="12px"
              border="1.5px solid"
              borderColor={i === 0 ? "accent" : "gray.200"}
              bg={i === 0 ? "rgba(229,31,68,0.06)" : "white"}
            >
              <Flex
                w="18px"
                h="18px"
                borderRadius="full"
                border="2px solid"
                borderColor={i === 0 ? "accent" : "gray.300"}
                align="center"
                justify="center"
                flexShrink={0}
              >
                {i === 0 && <Box w="9px" h="9px" borderRadius="full" bg="accent" />}
              </Flex>
              <Text fontSize="14px" color="primary" fontWeight={i === 0 ? "700" : "500"} noOfLines={1}>
                {o}
              </Text>
            </HStack>
          ))}
          {(frontMore > 0 || frontOptions.length > 3) && (
            <Text fontSize="12px" color="gray.500" fontWeight="600" pl="2px">
              +{frontMore + Math.max(0, frontOptions.length - 3)} więcej
            </Text>
          )}
        </VStack>

        <HStack
          as={Link}
          to={`/glosowanie/${front.id}`}
          mt="18px"
          justify="center"
          spacing="7px"
          py="12px"
          borderRadius="12px"
          border="1.5px solid"
          borderColor="rgba(67,0,152,0.18)"
          color="primary"
          fontWeight="700"
          fontSize="14px"
          transition="all 0.18s"
          _hover={{ bg: "rgba(67,0,152,0.05)", borderColor: "primary" }}
        >
          <Text>Zobacz głosowanie</Text>
          <FiArrowRight size={16} />
        </HStack>
      </Box>
    </Flex>
  );
};

interface HomeViewHeaderProps {
  votings: HeroVoting[];
}

export const HomeViewHeader: React.FC<HomeViewHeaderProps> = ({ votings }) => {
  return (
    <Box
      w="100%"
      position="relative"
      overflow="hidden"
      sx={{
        background: "linear-gradient(120deg, #2c0066, #430098, #5a0bbf, #7117d6, #5a0bbf, #430098, #2c0066)",
        backgroundSize: "300% 300%",
        animation: `${gradientShift} 16s ease infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" }
      }}
    >
      {/* pływające orby */}
      <Box
        position="absolute"
        top="-90px"
        left="-70px"
        w="340px"
        h="340px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(229,31,68,0.30), transparent 65%)"
        filter="blur(24px)"
        pointerEvents="none"
        zIndex={0}
        sx={{ animation: `${orbFloat} 19s ease-in-out infinite` }}
      />
      <Box
        position="absolute"
        bottom="-120px"
        right="8%"
        w="400px"
        h="400px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(138,74,243,0.45), transparent 65%)"
        filter="blur(24px)"
        pointerEvents="none"
        zIndex={0}
        sx={{ animation: `${orbFloat} 24s ease-in-out infinite reverse` }}
      />

      <Flex
        position="relative"
        zIndex={1}
        maxW="1240px"
        mx="auto"
        px={{ base: "24px", md: "32px" }}
        pt={{ base: "112px", md: "150px" }}
        pb={{ base: "48px", md: "80px" }}
        direction={{ base: "column", lg: "row" }}
        align="center"
        gap={{ base: "48px", lg: "56px" }}
      >
        <VStack flex="1" align={{ base: "center", lg: "flex-start" }} spacing="24px" textAlign={{ base: "center", lg: "left" }}>
          <HStack spacing="10px" flexWrap="wrap" justify={{ base: "center", lg: "flex-start" }}>
            <TrustChip icon={FiGift} label="Zawsze bezpłatne" />
            <TrustChip icon={FiLock} label="Wynik nie do podważenia" />
          </HStack>

          <Heading
            fontSize={{ base: "36px", md: "56px" }}
            lineHeight={{ base: "42px", md: "62px" }}
            fontWeight="800"
            color="white"
            letterSpacing="-0.02em"
          >
            Głosuj{" "}
            <Box as="span" color="accent">
              online
            </Box>
            <br />
            decyduj{" "}
            <Box as="span" color="accent">
              bezpośrednio
            </Box>
          </Heading>

          <Text fontSize={{ base: "17px", md: "20px" }} lineHeight={{ base: "26px", md: "30px" }} color="rgba(255,255,255,0.88)" maxW="560px">
            Darmowe i bezpieczne narzędzie do wiarygodnych głosowań w Twojej organizacji —
            stowarzyszeniu, spółdzielni, samorządzie uczniowskim czy lokalnej wspólnocie.
            Każdy głos chroni kryptografia i technologia blockchain.
          </Text>

          <Stack direction={{ base: "column", sm: "row" }} spacing="14px" w={{ base: "100%", sm: "auto" }} pt="4px">
            <Button
              as={Link}
              to="/rejestracja"
              h="auto"
              py="16px"
              px="30px"
              bg="accent"
              color="white"
              fontSize="16px"
              fontWeight="700"
              borderRadius="14px"
              boxShadow="0 12px 28px rgba(229,31,68,0.35)"
              _hover={{ bg: "#c41a3a", transform: "translateY(-1px)" }}
              _active={{ bg: "#a8152f" }}
              transition="all 0.15s"
            >
              Zorganizuj głosowanie
            </Button>
            <Button
              as={Link}
              to="/aktywne-glosowania"
              h="auto"
              py="16px"
              px="30px"
              bg="rgba(255,255,255,0.10)"
              color="white"
              fontSize="16px"
              fontWeight="700"
              borderRadius="14px"
              borderWidth="1.5px"
              borderColor="rgba(255,255,255,0.4)"
              _hover={{ bg: "rgba(255,255,255,0.2)" }}
            >
              Zobacz głosowania
            </Button>
          </Stack>
        </VStack>

        <VotingPreview votings={votings} />
      </Flex>
    </Box>
  );
};
